// services/workerClient.js — Shared Worker fetch + cache layer
// Eliminates duplication across finnhubAPI, fundsAPI, macroAPI.
// All API services use this single module for HTTP + caching + tracking.

import { API_BASE } from '../config';

// ── Shared in-memory cache ──────────────────────────────────
const cache = new Map();
const MAX_CACHE_SIZE = 200;

export function getCached(key, ttlMs) {
  const e = cache.get(key);
  if (!e) return null;
  if (Date.now() - e.ts < ttlMs) {
    e.ts = Date.now(); // LRU: refresh timestamp on access
    return e.data;
  }
  cache.delete(key); // expired — proactive cleanup
  return null;
}

export function setCache(key, data) {
  if (cache.size >= MAX_CACHE_SIZE && !cache.has(key)) {
    // Evict oldest entry (by timestamp) to stay within bounds
    let oldestKey = null, oldestTs = Infinity;
    for (const [k, v] of cache) {
      if (v.ts < oldestTs) { oldestTs = v.ts; oldestKey = k; }
    }
    if (oldestKey) cache.delete(oldestKey);
  }
  cache.set(key, { data, ts: Date.now() });
}

export function clearCache() {
  cache.clear();
}

// ── Auth header injection ────────────────────────────────────
export function getAuthHeaders() {
  const token = sessionStorage.getItem('sm_session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── 401 handler (session expired → redirect to login) ───────
let onUnauthorized = null;
export function setOnUnauthorized(fn) { onUnauthorized = fn; }

// ── API error tracking (H5) ─────────────────────────────────
// Components can subscribe to API errors for UI feedback.
const errorListeners = new Set();
export function onApiError(fn) { errorListeners.add(fn); return () => errorListeners.delete(fn); }
function notifyError(path, error) {
  errorListeners.forEach(fn => { try { fn({ path, error, timestamp: Date.now() }); } catch (e) { console.warn('Error listener threw:', e); } });
}

// ── Generic Worker fetch ────────────────────────────────────
// Single implementation — used by all API services.
// Injects session token from sessionStorage for authenticated requests.
export async function workerFetch(path) {
  const r = await fetch(API_BASE + path, {
    headers: getAuthHeaders(),
  });
  if (r.status === 401 && onUnauthorized) {
    onUnauthorized();
    throw new Error('Session expired');
  }
  if (!r.ok) {
    let body = null;
    try { body = await r.json(); } catch (_) { /* non-JSON response body, ignore */ }
    const msg = (body && body.error) || 'API error ' + r.status;
    notifyError(path, msg);
    throw new Error(msg);
  }
  return r.json();
}

// ── Cached fetch (combines fetch + cache + track in one call) ─
// Usage: const data = await cachedFetch('quote_AAPL', '/api/quote/AAPL', 30000, trackFn);
export async function cachedFetch(cacheKey, path, ttlMs, trackFn) {
  const c = getCached(cacheKey, ttlMs);
  if (c) {
    if (trackFn) trackFn(true);
    return c;
  }

  const d = await workerFetch(path);
  if (d != null) setCache(cacheKey, d);
  if (trackFn) trackFn(false);
  return d;
}

// ── Batch parallel fetch ────────────────────────────────────
// Fetches multiple items in parallel, returns { key: result | null }.
export async function fetchMultiple(items, fetchFn) {
  const results = {};
  await Promise.allSettled(
    items.map(async (key) => {
      try { results[key] = await fetchFn(key); }
      catch (e) { console.warn(`fetchMultiple: ${key} failed:`, e.message); results[key] = null; }
    })
  );
  return results;
}
